// ==================== MESSAGES SERVICE ====================

import { apiService } from './api';
import { getApiUrl, APP_CONSTANTS } from '../constants';

// ==================== TYPES ====================

export type MessageType = 'text' | 'image' | 'video' | 'audio' | 'document' | 'template' | 'interactive' | 'location';
export type MessageDirection = 'INBOUND' | 'OUTBOUND';
export type MessageStatus = 'PENDING' | 'SENT' | 'DELIVERED' | 'READ' | 'FAILED';

export interface TemplateButton {
  type: 'QUICK_REPLY' | 'URL' | 'PHONE' | 'COPY_CODE';
  text: string;
  url?: string;
  urlType?: 'STATIC' | 'DYNAMIC';
  phoneNumber?: string;
}

export interface InteractiveButton {
  id: string;
  title: string;
}

export interface MessageMetadata {
  templateId?: string;
  templateName?: string;
  headerType?: string;
  buttons?: TemplateButton[] | InteractiveButton[];
  // Interactive message type (e.g., 'button' for quick replies)
  interactiveType?: 'button' | 'list';
  // Location data
  latitude?: number;
  longitude?: number;
  name?: string;
  address?: string;
  // Document data
  filename?: string;
  // Contact vCard data
  contacts?: Array<{
    name?: string;
    phones?: string[];
  }>;
}

export interface Message {
  id: string;
  waMessageId?: string;
  direction: MessageDirection;
  type: MessageType;
  content: string | null;
  mediaUrl?: string;
  metadata?: MessageMetadata | null;
  status: MessageStatus;
  errorMessage?: string;
  sentAt?: string;
  deliveredAt?: string;
  readAt?: string;
  createdAt: string;
}

export interface Conversation {
  contactId: string;
  contactName: string;
  contactPhone: string;
  lastMessage: {
    content: string;
    type: MessageType;
    direction: MessageDirection;
    createdAt: string;
    status: MessageStatus;
  };
  unreadCount: number;
  lastMessageAt: string;
  isWithin24Hours: boolean;
}

export interface ConversationsResponse {
  items: Conversation[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface MessagesResponse {
  items: Message[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  isWithin24Hours: boolean;
  lastInboundAt: string | null;
  windowExpiresAt: string | null;
  contact: {
    id: string;
    name: string;
    phone: string;
  };
}

// ==================== SEND MESSAGE TYPES ====================

export interface TextMessage {
  body: string;
  previewUrl?: string;
}

export interface MediaMessage {
  id?: string;
  link?: string;
  caption?: string;
  filename?: string;
}

export interface LocationMessage {
  latitude: number;
  longitude: number;
  name?: string;
  address?: string;
}

export interface InteractiveButton {
  id: string;
  title: string;
}

export interface InteractiveButtonsMessage {
  header?: string;
  body: string;
  footer?: string;
  buttons: InteractiveButton[];
}

export interface ListRow {
  id: string;
  title: string;
  description?: string;
}

export interface ListSection {
  title: string;
  rows: ListRow[];
}

export interface InteractiveListMessage {
  header?: string;
  body: string;
  footer?: string;
  buttonText: string;
  sections: ListSection[];
}

export interface TemplateParameter {
  type: string;
  text?: string;
  image?: { link: string };
  video?: { link: string };
  document?: { link: string; filename?: string };
}

export interface TemplateComponent {
  type: string;
  subType?: string;
  index?: number;
  parameters?: TemplateParameter[];
}

export interface TemplateMessage {
  templateId: string;
  components?: TemplateComponent[];
}

export interface SendMessageData {
  /** Multiple organizations: the account (number) the chat is with */
  organizationId?: string;
  contactId: string;
  type: MessageType;
  text?: TextMessage;
  image?: MediaMessage;
  video?: MediaMessage;
  audio?: MediaMessage;
  document?: MediaMessage;
  location?: LocationMessage;
  template?: TemplateMessage;
  interactiveType?: 'button' | 'list';
  interactiveButtons?: InteractiveButtonsMessage;
  interactiveList?: InteractiveListMessage;
}

// ==================== SERVICE ====================

export const messagesService = {
  /**
   * Get all conversations
   */
  async getConversations(params?: { search?: string; page?: number; limit?: number; organizationId?: string }): Promise<ConversationsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.organizationId) queryParams.append('organizationId', params.organizationId);

    const query = queryParams.toString();
    const response = await apiService.get(`/customer/messages/conversations${query ? `?${query}` : ''}`);
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch conversations');
    }

    return response.data as ConversationsResponse;
  },

  /**
   * Get messages for a contact
   */
  async getMessages(contactId: string, params?: { page?: number; limit?: number; order?: 'asc' | 'desc'; organizationId?: string }): Promise<MessagesResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('contactId', contactId);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.order) queryParams.append('order', params.order);
    if (params?.organizationId) queryParams.append('organizationId', params.organizationId);

    const response = await apiService.get(`/customer/messages?${queryParams.toString()}`);
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch messages');
    }

    return response.data as MessagesResponse;
  },

  /**
   * Send a message
   */
  async sendMessage(data: SendMessageData): Promise<Message> {
    const response = await apiService.post('/customer/messages/send', data);
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to send message');
    }

    return response.data as Message;
  },

  /**
   * Mark messages as read for a contact
   */
  async markAsRead(contactId: string, organizationId?: string): Promise<void> {
    const response = await apiService.post(`/customer/messages/${contactId}/read${organizationId ? `?organizationId=${encodeURIComponent(organizationId)}` : ''}`, {});
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to mark messages as read');
    }
  },

  /**
   * Upload media file for messaging
   * Returns a media ID that can be used when sending image/video/document/audio messages
   * Media IDs expire after 30 days
   * organizationId: upload through the selected account's number
   */
  async uploadMedia(
    file: File, 
    mediaType: 'image' | 'video' | 'document' | 'audio',
    onProgress?: (progress: number) => void,
    organizationId?: string,
  ): Promise<{ mediaId: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mediaType', mediaType);
    if (organizationId) formData.append('organizationId', organizationId);

    const response = await apiService.upload<{ mediaId: string }>('/customer/messages/upload-media', formData, onProgress);
    
    // Standard API response check
    if (!response.success) {
      throw new Error(response.message || 'Failed to upload media');
    }

    // Extract mediaId from response.data (standard wrapped format)
    const mediaId = response.data?.mediaId;
    if (!mediaId) {
      throw new Error('No media ID returned from server');
    }

    return { mediaId };
  },

  /**
   * Proxy media from Meta WhatsApp (requires authentication)
   * Fetches media through backend proxy and returns a blob URL
   */
  async proxyMedia(mediaUrl: string): Promise<string> {
    const token = localStorage.getItem(APP_CONSTANTS.STORAGE_KEYS.ACCESS_TOKEN);
    const baseUrl = getApiUrl();
    
    const response = await fetch(
      `${baseUrl}/customer/messages/media/proxy?url=${encodeURIComponent(mediaUrl)}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch media: ${response.status}`);
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },
};
